import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  Pagination,
} from "@mui/material";
import CommonBreadcrumb from "../../components/common/CommonBreadcrumb";
import { AdminApi } from "../../api/AdminApi";

const PAGE_SIZE = 20;

const ACTION_LABEL = {
  set_username: "設定會員 username",
  reset_all_passwords: "重置全部密碼",
  set_one_password: "設定會員密碼",
  reset_password: "重置密碼",
  import: "匯入會員",
  create_guest: "創建Guest賬號",
};

const CHANGE_LABEL = {
  created: "新增",
  updated: "更新",
  deleted: "刪除",
};

const MEMBER_IMPORT_TEMPLATE =
  "\uFEFF" +
  "cls,cls_num,name_zh,name_en,username,group,group_num,gender,member_id\n";

const SCOPE_LABEL = {
  all: "全部會員",
  missing_username: "沒有帳號的會員",
  one: "單一會員",
};

const RESULT_LABEL = {
  success: "成功",
  failure: "失敗",
};

const STATUS_LABEL = {
  pending: "處理中",
  success: "成功",
  fail: "失敗",
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const IMPORT_TABS = [
  { value: "created", label: "新增", countKey: "created_count" },
  { value: "updated", label: "更新", countKey: "updated_count" },
  { value: "deleted", label: "刪除", countKey: "deleted_count" },
];

const firstImportTab = (result) => {
  const match = IMPORT_TABS.find((tab) => Number(result?.[tab.countKey] || 0) > 0);
  return match ? match.value : "created";
};

const emptyTool = () => ({
  action: "",
  scope: "all",
  passwordMode: "random",
  password: "",
  username: "",
  file: null,
  guestCount: "",
  matchBy: "name",
  removeMissing: "no",
});

export default function OrganizationUsernameSetup() {
  const { orgId } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [phase, setPhase] = useState("form");
  const [tool, setTool] = useState(emptyTool());
  const [preview, setPreview] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [page, setPage] = useState(1);
  const [importTab, setImportTab] = useState("created");

  const loadSummary = async () => {
    setLoading(true);
    try {
      const result = await AdminApi.getUsernameSetupSummary(orgId);
      setSummary(result);
    } catch (error) {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, [orgId]);

  const openTool = (action) => {
    setTool({
      ...emptyTool(),
      action,
      scope: action === "set_one_password" ? "one" : "all",
    });
    setPreview(null);
    setConfirmCode("");
    setPage(1);
    setImportTab("created");
    setPhase("form");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (loading) {
      return;
    }
    setDialogOpen(false);
  };

  const buildPayload = () => ({
    action: tool.action,
    scope:
      tool.action === "set_username"
        ? tool.scope
        : tool.action === "set_one_password"
          ? "one"
          : "",
    password_mode: tool.passwordMode,
    password: tool.passwordMode === "custom" ? tool.password.trim() : "",
    username: tool.action === "set_one_password" ? tool.username.trim() : "",
  });

  const onStart = async () => {
    if (tool.action === "set_one_password" && tool.username.trim() === "") {
      toast.error("請輸入會員 username");
      return;
    }
    if (
      tool.passwordMode === "custom" &&
      /^[A-Za-z0-9]{6,32}$/.test(tool.password.trim()) === false
    ) {
      toast.error("自定義密碼需要 6 至 32 位英文字母或數字");
      return;
    }
    if (tool.action === "import") {
      if (!tool.file) {
        toast.error("請選擇 CSV 檔案");
        return;
      }
      if (tool.file.name.toLowerCase().endsWith(".csv") === false) {
        toast.error("請上載 CSV 檔案");
        return;
      }
    }
    if (
      tool.action === "create_guest" &&
      /^[1-9][0-9]*$/.test(tool.guestCount.trim()) === false
    ) {
      toast.error("請輸入建立人數");
      return;
    }

    setLoading(true);
    try {
      let result;
      if (tool.action === "import") {
        result = await AdminApi.previewMemberImport(
          orgId,
          tool.file,
          tool.passwordMode,
          tool.password,
          tool.matchBy,
          tool.removeMissing
        );
      } else if (tool.action === "create_guest") {
        result = await AdminApi.previewGuestAccounts(
          orgId,
          tool.guestCount,
          tool.passwordMode,
          tool.password
        );
      } else {
        result = await AdminApi.previewUsernameSetup(orgId, buildPayload());
      }
      setPreview(result);
      setPage(1);
      setImportTab(tool.action === "import" ? firstImportTab(result) : "created");
      setConfirmCode("");
      setPhase(result?.ok ? "confirm" : "failures");
    } catch (error) {
      setPhase("form");
    } finally {
      setLoading(false);
    }
  };

  const onCommit = async () => {
    setLoading(true);
    try {
      let result;
      if (tool.action === "import") {
        result = await AdminApi.commitMemberImport(
          orgId,
          tool.file,
          tool.passwordMode,
          tool.password,
          tool.matchBy,
          tool.removeMissing
        );
        toast.success(`匯入成功，共 ${result?.affected_count || 0} 筆`);
      } else if (tool.action === "create_guest") {
        result = await AdminApi.commitGuestAccounts(
          orgId,
          tool.guestCount,
          tool.passwordMode,
          tool.password
        );
        toast.success(`已建立 ${result?.affected_count || 0} 個 Guest 賬號`);
      } else {
        result = await AdminApi.commitUsernameSetup(orgId, buildPayload());
        toast.success(`設定成功，共 ${result?.affected_count || 0} 位會員`);
      }
      if (result?.csv && result?.filename) {
        downloadCsv(result.filename, result.csv);
      }
      setDialogOpen(false);
      await loadSummary();
    } catch (error) {
      await loadSummary();
    } finally {
      setLoading(false);
    }
  };

  const needsOrgCode =
    tool.action === "reset_all_passwords" ||
    tool.action === "import" ||
    tool.action === "create_guest" ||
    (tool.action === "set_username" && tool.scope === "all");
  const orgCode = (summary?.org_code || "").trim();
  const codeMatches = confirmCode.trim() === orgCode;
  const affectedCount = Number(preview?.affected_count ?? 0);
  const sourceRows =
    phase === "failures" ? preview?.failures || [] : preview?.members || [];
  const listRows =
    phase === "confirm" && tool.action === "import"
      ? sourceRows.filter((row) => row.change_type === importTab)
      : sourceRows;
  const pageCount = Math.max(1, Math.ceil(listRows.length / PAGE_SIZE));
  const visibleRows = listRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container maxWidth="lg">
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CommonBreadcrumb
        dbData={{
          organization_id: orgId,
          organization_name: summary?.org_name || "",
        }}
      />
      <Grid container columnSpacing={2}>
        <Grid item xs={9}>
          <Paper sx={{ px: 3, py: 4, mt: 3 }} elevation={3}>
            <Typography variant="h6" color="text.secondary">
              會員設定工具
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
              <Typography>會員人數：{summary?.member_count ?? "-"}</Typography>
              <Typography>
                已有 username：{summary?.username_count ?? "-"}
              </Typography>
            </Stack>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              最近 10 次設定紀錄
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>時間</TableCell>
                  <TableCell>帳號</TableCell>
                  <TableCell>動作</TableCell>
                  <TableCell>範圍</TableCell>
                  <TableCell>人數</TableCell>
                  <TableCell>失敗</TableCell>
                  <TableCell>結果</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary?.logs || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>沒有紀錄</TableCell>
                  </TableRow>
                ) : (
                  summary.logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.created_at}</TableCell>
                      <TableCell>{log.admin_account}</TableCell>
                      <TableCell>{ACTION_LABEL[log.action] || log.action}</TableCell>
                      <TableCell>{SCOPE_LABEL[log.scope] || ""}</TableCell>
                      <TableCell>{log.affected_count}</TableCell>
                      <TableCell>{log.failed_count ?? 0}</TableCell>
                      <TableCell>
                        {STATUS_LABEL[log.status] ||
                          RESULT_LABEL[log.result] ||
                          log.status ||
                          log.result}
                        {(log.status === "fail" || log.result === "failure") &&
                        log.message
                          ? `（${log.message}）`
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ px: 3, py: 4, mt: 3 }} elevation={3}>
            <Stack spacing={2}>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => openTool("set_username")}
              >
                設定會員 username
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={loading}
                onClick={() => openTool("reset_all_passwords")}
              >
                重置全部密碼
              </Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => openTool("set_one_password")}
              >
                設定會員密碼
              </Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => openTool("import")}
              >
                匯入會員
              </Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => openTool("create_guest")}
              >
                創建Guest賬號
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{ACTION_LABEL[tool.action] || "設定"}</DialogTitle>
        <DialogContent>
          {phase === "form" && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {tool.action === "set_username" && (
                <FormControl>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    會員範圍
                  </Typography>
                  <RadioGroup
                    value={tool.scope}
                    onChange={(event) =>
                      setTool({ ...tool, scope: event.target.value })
                    }
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label="全部會員"
                    />
                    <FormControlLabel
                      value="missing_username"
                      control={<Radio />}
                      label="沒有帳號的會員"
                    />
                  </RadioGroup>
                </FormControl>
              )}
              {tool.action === "set_one_password" && (
                <TextField
                  label="會員 username"
                  value={tool.username}
                  onChange={(event) =>
                    setTool({ ...tool, username: event.target.value })
                  }
                  fullWidth
                />
              )}
              {tool.action === "import" && (
                <Box>
                  <Typography variant="subtitle2">使用說明：</Typography>
                  <Typography component="ul" variant="body2" sx={{ m: 0, pl: 2.5 }}>
                    <li>下載IMPORT CSV模板並加入學生資料</li>
                    <li>必須填寫 班別，班號，名字(name_zh, name_en)</li>
                    <li>
                      如果已經註冊，請填寫username 及選用「使用 username匹配」
                    </li>
                  </Typography>
                </Box>
              )}
              {tool.action === "import" && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="outlined" component="label">
                    選擇 CSV
                    <input
                      type="file"
                      hidden
                      accept=".csv,text/csv"
                      onChange={(event) => {
                        const file = event.target.files && event.target.files[0];
                        setTool({ ...tool, file: file || null });
                      }}
                    />
                  </Button>
                  <Typography sx={{ flex: 1 }}>
                    {tool.file ? tool.file.name : "未選擇檔案"}
                  </Typography>
                  <Button
                    onClick={() =>
                      downloadCsv("member-import-template.csv", MEMBER_IMPORT_TEMPLATE)
                    }
                  >
                    下載CSV模板
                  </Button>
                </Stack>
              )}
              {tool.action === "import" && (
                <FormControl>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    匹配會員方法
                  </Typography>
                  <RadioGroup
                    value={tool.matchBy}
                    onChange={(event) =>
                      setTool({ ...tool, matchBy: event.target.value })
                    }
                  >
                    <FormControlLabel
                      value="name"
                      control={<Radio />}
                      label="使用名字（需要自行處理名字重複問題）"
                    />
                    <FormControlLabel
                      value="username"
                      control={<Radio />}
                      label="使用 username（新用戶可以空白）"
                    />
                  </RadioGroup>
                </FormControl>
              )}
              {tool.action === "import" && (
                <FormControl>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    是否刪除不在清單的會員？
                  </Typography>
                  <RadioGroup
                    row
                    value={tool.removeMissing}
                    onChange={(event) =>
                      setTool({ ...tool, removeMissing: event.target.value })
                    }
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="YES" />
                    <FormControlLabel value="no" control={<Radio />} label="NO" />
                  </RadioGroup>
                </FormControl>
              )}
              {tool.action === "create_guest" && (
                <TextField
                  label="建立人數"
                  value={tool.guestCount}
                  onChange={(event) =>
                    setTool({ ...tool, guestCount: event.target.value })
                  }
                  fullWidth
                />
              )}
              <FormControl>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  密碼
                </Typography>
                <RadioGroup
                  value={tool.passwordMode}
                  onChange={(event) =>
                    setTool({ ...tool, passwordMode: event.target.value })
                  }
                >
                  <FormControlLabel
                    value="random"
                    control={<Radio />}
                    label="6位隨機數字"
                  />
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label="自定義密碼"
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                label="自定義密碼"
                value={tool.password}
                disabled={tool.passwordMode !== "custom"}
                onChange={(event) =>
                  setTool({ ...tool, password: event.target.value })
                }
                fullWidth
              />
            </Stack>
          )}

          {phase === "confirm" && (
            <Box sx={{ mt: 1 }}>
              {tool.action === "import" && (
                <Typography sx={{ mb: 1 }}>
                  是否刪除不在清單的會員：{tool.removeMissing === "yes" ? "YES" : "NO"}
                </Typography>
              )}
              {tool.action === "import" && (
                <Tabs
                  value={importTab}
                  onChange={(event, value) => {
                    setImportTab(value);
                    setPage(1);
                  }}
                  sx={{ mb: 1 }}
                >
                  {IMPORT_TABS.map((tab) => (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={`${tab.label} ${preview?.[tab.countKey] || 0}`}
                    />
                  ))}
                </Tabs>
              )}
              {tool.action === "import" && (preview?.warnings || []).length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    檔案有重複資料，仍可繼續匯入
                  </Typography>
                  <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                    {(preview.warnings || []).map((row, index) => (
                      <Typography key={`${row.line}-${row.member_id}-${index}`} variant="body2">
                        {row.reason}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}
              {tool.action === "create_guest" && (
                <Typography sx={{ mb: 1 }}>
                  訪客編號 {preview?.first_number} 至 {preview?.last_number}
                </Typography>
              )}
              <Typography sx={{ mb: 2 }}>
                {affectedCount} affected
              </Typography>
              <MemberTable
                rows={visibleRows}
                mode="confirm"
                showChange={tool.action === "import" || tool.action === "create_guest"}
              />
              {pageCount > 1 && (
                <Pagination
                  sx={{ mt: 2 }}
                  count={pageCount}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                />
              )}
            </Box>
          )}

          {phase === "failures" && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ mb: 2 }}>
                {preview?.message || "以下會員未能設定"}
              </Typography>
              <MemberTable rows={visibleRows} mode="failures" />
              {pageCount > 1 && (
                <Pagination
                  sx={{ mt: 2 }}
                  count={pageCount}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ alignItems: "center", px: 3, pb: 2 }}>
          {phase === "form" && (
            <>
              <Button onClick={closeDialog} disabled={loading}>
                取消
              </Button>
              <Button variant="contained" onClick={onStart} disabled={loading}>
                開始設定
              </Button>
            </>
          )}
          {phase === "confirm" && (
            <>
              {needsOrgCode && (
                <TextField
                  size="small"
                  label="輸入機構代碼以確認"
                  value={confirmCode}
                  onChange={(event) => setConfirmCode(event.target.value)}
                  sx={{ width: 240, mr: 1 }}
                />
              )}
              <Button onClick={closeDialog} disabled={loading}>
                NO
              </Button>
              <Button
                variant="contained"
                onClick={onCommit}
                disabled={
                  loading ||
                  affectedCount === 0 ||
                  (needsOrgCode && !codeMatches)
                }
              >
                YES
              </Button>
            </>
          )}
          {phase === "failures" && (
            <Button onClick={() => setPhase("form")} disabled={loading}>
              返回
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function usernameCell(row) {
  if (row.username_action === "update" && row.previous_username) {
    return `${row.previous_username} -> ${row.username}`;
  }
  return row.username || "";
}

function usernameActionLabel(row) {
  if (row.username_action === "create") {
    return "建立";
  }
  if (row.username_action === "update") {
    return "更新";
  }
  return "保留";
}

function MemberTable({ rows, mode, showChange = false }) {
  const columnCount = mode === "confirm" ? (showChange ? 7 : 6) : 5;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>cls</TableCell>
          <TableCell>cls_num</TableCell>
          <TableCell>名字</TableCell>
          <TableCell>member_id</TableCell>
          {mode === "confirm" ? (
            <>
              <TableCell>username</TableCell>
              <TableCell>處理</TableCell>
              {showChange && <TableCell>變更</TableCell>}
            </>
          ) : (
            <TableCell>原因</TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount}>沒有會員</TableCell>
          </TableRow>
        ) : (
          rows.map((row, index) => (
            <TableRow key={`${row.change_type || ""}-${row.id}-${row.line || index}-${row.member_id}`}>
              <TableCell>{row.school_cls}</TableCell>
              <TableCell>{row.cls_num}</TableCell>
              <TableCell>{row.display_name}</TableCell>
              <TableCell>{row.member_id}</TableCell>
              {mode === "confirm" ? (
                <>
                  <TableCell>{usernameCell(row)}</TableCell>
                  <TableCell>{usernameActionLabel(row)}</TableCell>
                  {showChange && (
                    <TableCell>{CHANGE_LABEL[row.change_type] || row.change_type}</TableCell>
                  )}
                </>
              ) : (
                <TableCell>{row.reason}</TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
